import { useContext, useEffect, useState, useMemo } from "react";
import { ControlContext } from "../../ControlContext.jsx";
import { useLoader } from "@react-three/fiber";
import { textures, materialConfigs } from "src/utils/PaperFinishConfigs.js";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { withCrossOrigin } from "../../utils/ensureTextureCrossOrigin";
import { RepeatWrapping, Vector2 } from "three";

export default function PaperFinish({ depth }) {
  const {
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    paperType,
    faceMountFinish,
    frameType,
  } = useContext(ControlContext);

  const [totalWidth, setTotalWidth] = useState(0);
  const [totalHeight, setTotalHeight] = useState(0);
  const [displayType, setDisplayType] = useState("none");

  const roughnessMaps = useMemo(() => {
    const maps = {};
    Object.entries(textures.roughness).forEach(([key, path]) => {
      maps[key] = useLoader(TextureLoader, path, withCrossOrigin);
    });
    return maps;
  }, []);

  const normalMaps = useMemo(() => {
    const maps = {};
    Object.entries(textures.normal).forEach(([key, path]) => {
      maps[key] = useLoader(TextureLoader, path, withCrossOrigin);
    });
    return maps;
  }, []);

  useEffect(() => {
    setTotalWidth(printWidth + borderWidth * 2);
    setTotalHeight(printHeight + borderHeight * 2);
  }, [printWidth, printHeight, borderWidth, borderHeight]);

  useEffect(() => {
    if (frameType === "Box" || frameType === "Aluminum") {
      setDisplayType("none");
      return;
    }
    
    if (faceMountFinish === "Paper" || faceMountFinish === "Perspex") {
      setDisplayType("none");
    } else {
      setDisplayType(faceMountFinish);
    }
  }, [paperType, faceMountFinish, frameType]);

  const materialProps = useMemo(() => {
    const config = materialConfigs[displayType] || materialConfigs["none"];
    const result = { ...config };
    
    const textureScale = config.textureScale || 1;

    if (
      config.clearcoatRoughnessMapType &&
      roughnessMaps[config.clearcoatRoughnessMapType]
    ) {
      const roughnessMap = roughnessMaps[config.clearcoatRoughnessMapType].clone();
      
      roughnessMap.wrapS = roughnessMap.wrapT = RepeatWrapping;
      roughnessMap.repeat.set(textureScale, textureScale);
      
      result.clearcoatRoughnessMap = roughnessMap;
      result.clearcoatRoughness = config.clearcoatRoughness;
    }

    // Apply normal map if specified
    if (config.normalMapType && normalMaps[config.normalMapType]) {
      const normalMap = normalMaps[config.normalMapType].clone();
      
      normalMap.wrapS = normalMap.wrapT = RepeatWrapping;
      normalMap.repeat.set(textureScale, textureScale);
      
      result.normalMap = normalMap;
      
      if (config.normalScale) {
        result.normalScale = new Vector2(config.normalScale[0], config.normalScale[1]);
      }
    }

    delete result.clearcoatRoughnessMapType;
    delete result.normalMapType;
    delete result.textureScale;

    return result;
  }, [displayType, roughnessMaps, normalMaps]);

  // console.log("displayType:", displayType);
  // console.log("materialProps:", materialProps);

  // Only render the mesh if displayType is not "none"
  if (displayType === "none") {
    return null;
  }

  return (
    <mesh position={[0, 0, depth + 0.1]}>
      <planeGeometry args={[totalWidth, totalHeight]} />
      <meshPhysicalMaterial {...materialProps} />
    </mesh>
  );
}
